import { noop } from 'lodash-es'
import get from 'lodash-es/get'
import TileLayer from '@arcgis/core/layers/TileLayer.js'
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer.js'

import { BASEMAPS, CONTAINER_IDS, FEATURE_TYPES, MAP_LAYER_IDS, MAP_MODES, MAP_TOOLS, MEASURING_TOOLS, STATES_DATA } from '../../lib/constants'
import { getActiveViewRef, getFeatureGraphic, getFeatureSymbol, getGhostWaypointGraphic, getLabelGraphic, getTerrain } from '../../lib/map'
import { getConfig } from '../../lib/config'
import { getItemIndex } from '../../lib/utils'
import { reverseGeocode } from '../../services/state'
import ghAnalytics from '../../lib/analytics'

/***************
 * EFFECTS
 ***************/

export function* refreshElevation(_, { select, call }) {
  const { mapRef, mapMode } = yield select(model => model.map)
  const { profile } = yield select(model => model.user)

  if (mapMode == MAP_MODES.ThreeD) {
    const terrain = yield call(getTerrain, profile.mapPreferences.elevationExaggeration)
    mapRef.ground = {
      layers: [terrain]
    }
  }
}

// Terrain layers are expensive to recreate — cache by exaggeration value so the
// same layer instance is reused on every 3D switch. Cap at 3 entries so repeated
// exaggeration changes on low-spec devices don't accumulate stale layers.
const TERRAIN_CACHE_MAX = 3
const terrainLayerCache = new Map()

export function* changeMapMode({ payload: mode }, { select, put, call }) {
  const { mapMode, mapRef, mapViewRef, sceneViewRef } = yield select(model => model.map)
  const { mapPreferences } = yield select(model => model.user?.profile)

  const isSwitchingTo2D = mode === MAP_MODES.TwoD && mapMode !== MAP_MODES.TwoD
  const isSwitchingTo3D = mode === MAP_MODES.ThreeD && mapMode !== MAP_MODES.ThreeD

  if (mapRef != null && mapViewRef != null && sceneViewRef != null) {
    // /*
    // 	Removes internal graphics layer, which is used for our state layers,
    //   since a new one will be created when switching between 2D/3D.
    // 	Needs testing to ensure no side effects occur.
    // */
    // const mapLayerItems = mapRef.layers.items
    // const layerIndex = getItemIndex({
    // 	items: mapLayerItems,
    // 	key: 'id',
    // 	reverse: true,
    // 	value: MAP_LAYER_IDS.gohunt,
    // })
    // if (layerIndex > -1 && mapLayerItems.length - 1 >= layerIndex + 1) {
    // 	mapRef.remove(mapLayerItems[layerIndex + 1])
    // }
    // /****************************************************************************/
    if (isSwitchingTo2D) {
      mapRef.ground.layers.removeAll()
      // Set viewpoint before making the view visible to avoid a one-frame positional flash.
      mapViewRef.viewpoint = sceneViewRef.viewpoint.clone()
      mapViewRef.container = CONTAINER_IDS.map

      sceneViewRef.container = null
    } else if (isSwitchingTo3D) {
      // Fallback exaggeration of 1 if preferences haven't loaded yet.
      const exaggeration = mapPreferences?.elevationExaggeration ?? 1
      if (!terrainLayerCache.has(exaggeration)) {
        if (terrainLayerCache.size >= TERRAIN_CACHE_MAX) {
          terrainLayerCache.delete(terrainLayerCache.keys().next().value)
        }
        terrainLayerCache.set(exaggeration, yield call(getTerrain, exaggeration))
      }
      Object.assign(mapRef.ground, {
        layers: [terrainLayerCache.get(exaggeration)],
        surfaceColor: [10, 60, 100]
      })
      // Set viewpoint before making the view visible to avoid a one-frame positional flash.
      sceneViewRef.viewpoint = mapViewRef.viewpoint.clone()
      sceneViewRef.container = CONTAINER_IDS.map

      mapViewRef.container = null
    }
    yield put({ type: 'setMapMode', payload: mode })
  }
}

export function* cancelActiveTool(_, { select, put }) {
  const { mapRef, widgetRefs, activeTool } = yield select(model => model.map)

  if (mapRef != null) {
    /*
      #NOTE: Keep these separate, don't merge the conditions. There could be extra things to do upon cancel in future.
      -- KD - July 28, 2020
    */
    if (activeTool === MAP_TOOLS.Select) {
      const selectSketchRef = widgetRefs.selectSketch
      if (selectSketchRef != null) {
        selectSketchRef.cancel()
      }
    } else if (activeTool === MAP_TOOLS.Sketch_Point || activeTool === MAP_TOOLS.Sketch_Polyline || activeTool === MAP_TOOLS.Sketch_Polygon) {
      const sketchRef = widgetRefs.sketch
      if (sketchRef != null) {
        sketchRef.cancel()
      }
    } else if (activeTool === MAP_TOOLS.Measure) {
      const measurementRef = widgetRefs.measurement
      if (measurementRef != null) {
        measurementRef.clear()
        measurementRef.activeTool = null
      }
    }
  }
  yield put({ type: 'setActiveTool', payload: null })
}

export function* enableSketchPointTool(_, { select }) {
  const { mapRef, widgetRefs } = yield select(model => model.map)
  const sketchRef = widgetRefs.sketch

  if (mapRef != null && sketchRef != null) {
    sketchRef.create('point')
  }
}

export function* enableSketchPolylineTool(_, { select }) {
  const { mapRef, widgetRefs } = yield select(model => model.map)
  const sketchRef = widgetRefs.sketch

  if (mapRef != null && sketchRef != null) {
    sketchRef.create('polyline')
  }
}

export function* enableSketchPolygonTool(_, { select }) {
  const { mapRef, widgetRefs } = yield select(model => model.map)
  const sketchRef = widgetRefs.sketch

  if (mapRef != null && sketchRef != null) {
    sketchRef.create('polygon')
  }
}

export function* enableSelectTool(_, { select }) {
  const { mapRef, widgetRefs } = yield select(model => model.map)
  const selectSketchRef = widgetRefs.selectSketch

  if (mapRef != null && selectSketchRef != null) {
    selectSketchRef.create('rectangle')
  }
}

export function* enableLineMeasurement(action, { select, put }) {
  const { mapRef, widgetRefs, mapMode } = yield select(model => model.map)
  const measureRef = widgetRefs.measurement

  if (mapRef != null && measureRef != null) {
    const activeTool = mapMode === MAP_MODES.TwoD ? MEASURING_TOOLS.Distance : MEASURING_TOOLS.DirectLine
    measureRef.activeTool = activeTool
    yield put({ type: 'setActiveMeasureTool', payload: activeTool })

    ghAnalytics.logMeasureTool()
  }
}

export function* clearMeasurements(action, { select, put }) {
  const { mapRef, widgetRefs } = yield select(model => model.map)
  const measureRef = widgetRefs.measurement
  const elevProfRef = widgetRefs.elevationProfile

  if (mapRef != null && measureRef != null) {
    measureRef.clear()
    measureRef.activeTool = null
    elevProfRef.viewModel.clear()

    yield put({ type: 'setActiveMeasureTool', payload: null })
  }
}

export function* clearLayerGraphics({ payload }, { select, put, call }) {
  const { mapRef } = yield select(model => model.map)
  const { layerId } = payload

  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)

    if (layer) {
      layer.removeAll()
    }
  }
}

export function* clearGhostLayer(_, { select }) {
  const { mapRef } = yield select(model => model.map)

  if (mapRef) {
    const layerId = MAP_LAYER_IDS.ghost
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      layer.removeAll()
    }
  }
}

export function* renderGhostFeatureGraphic({ payload }, { select, put, call }) {
  const { mapRef } = yield select(model => model.map)

  const { feature } = payload

  if (mapRef) {
    const layerId = MAP_LAYER_IDS.ghost
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      const featureGraphic = yield call(getGhostWaypointGraphic, feature)

      if (featureGraphic != null) {
        yield put({
          type: 'addGraphicToMap',
          payload: {
            graphic: featureGraphic,
            layerId
          }
        })
      }
    }
  }
}

/************************************************************************
 * Bulk calls
 ************************************************************************/

export function* renderMultipleFeatureGraphics({ callback = noop, payload }, { select, put }) {
  const { mapRef } = yield select(model => model.map)
  const { featuresList, layerId } = payload
  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      const graphics = []
      for (let fli = 0; fli < featuresList.length; fli++) {
        let feature = getFeatureGraphic(featuresList[fli])
        feature.visible = feature.attributes.isVisible
        graphics.push(feature)
      }
      if (graphics.length) {
        yield put({
          type: 'addMultipleGraphicsToMap',
          payload: {
            graphics,
            layerId
          }
        })
      }
    }
    callback()
  }
}

export function* removeMultipleFeatureGraphicsById({ callback = noop, payload }, { select, put }) {
  const { mapRef } = yield select(model => model.map)
  const { featureIds, layerId } = payload
  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      const graphics = []
      for (let fidi = 0; fidi < featureIds.length; fidi++) {
        const graphicIndex = layer.graphics.items.findIndex(i => i.attributes.uuid === featureIds[fidi])
        if (graphicIndex >= -1) {
          graphics.push(layer.graphics.items[graphicIndex])
        }
      }
      if (graphics.length) {
        yield put({
          type: 'removeMultipleGraphicsFromMap',
          payload: {
            graphics,
            layerId
          }
        })
      }
    }
    callback()
  }
}

export function* setAllFeatureGraphicsVisibility({ callback = noop, payload }, { select }) {
  const { mapRef } = yield select(model => model.map)
  const { layerId, visibility } = payload

  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      for (let i = 0; i < layer.graphics.items.length; i++) {
        layer.graphics.items[i].visible = visibility
      }
    }
  }
  callback()
}

export function* setFeatureGraphicsVisibilityById({ callback = noop, payload }, { select }) {
  const { mapRef } = yield select(model => model.map)
  const { featureIds, layerId, visibility } = payload
  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      for (let i = 0; i < layer.graphics.items.length; i++) {
        if (featureIds.includes(layer.graphics.items[i].attributes.id)) {
          layer.graphics.items[i].visible = visibility
        }
      }
    }
  }
  callback()
}

export function* addMultipleGraphicsToMap({ payload }, { select }) {
  const { mapRef } = yield select(model => model.map)
  const { graphics, layerId } = payload

  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      let graphic = null
      for (let gi = 0; gi < graphics.length; gi++) {
        graphic = graphics[gi]
        if (graphic.attributes.properties.ghType === FEATURE_TYPES.Point) {
          layer.graphics.push(graphic)
        } else {
          layer.graphics.unshift(graphic)
        }
      }
    }
  }
}
export function* removeMultipleGraphicsFromMap({ payload }, { select }) {
  const { mapRef } = yield select(model => model.map)
  const { graphics, layerId } = payload

  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      const newLayerGraphicItems = []
      const graphicIds = graphics.map(g => g.attributes.uuid)
      for (let i = 0; i < layer.graphics.items.length; i++) {
        if (!graphicIds.includes(layer.graphics.items[i].attributes.uuid)) {
          newLayerGraphicItems.push(layer.graphics.items[i])
        }
      }
      layer.graphics.items = newLayerGraphicItems
    }
  }
}

export function* updateMultipleFeatureGraphics({ payload }, { select }) {
  const { mapRef } = yield select(model => model.map)
  const { features, layerId } = payload
  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      for (let fi = 0; fi < features.length; fi++) {
        const feature = features[fi]
        const graphicIndex = layer.graphics.findIndex(g => g.attributes.uuid === feature.uuid)
        if (graphicIndex > -1) {
          const graphic = layer.graphics.items[graphicIndex]
          graphic.symbol = getFeatureSymbol(feature)
          if (feature.properties.ghType === FEATURE_TYPES.Point) {
            graphic.geometry = {
              type: 'point',
              longitude: feature.geometry.coordinates[0],
              latitude: feature.geometry.coordinates[1],
              spatialReference: {
                wkid: 3857 // This is important for when editing waypoint location.
              }
            }
          }
        }
      }
    }
  }
}
/************************************************************************/

export function* renderFeatureGraphic({ payload }, { select, put, call }) {
  const { mapRef } = yield select(model => model.map)
  /*
    #TODO: Can we assume that the feature will always go to the content layer?
    -- TJC - May 12, 2020
  */
  const { feature, layerId } = payload

  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      const graphic = getFeatureGraphic(feature)
      if (graphic) {
        yield put({
          type: 'addGraphicToMap',
          payload: {
            graphic,
            layerId
          }
        })
        const goto = get(payload, ['goto'], false)
        if (goto) {
          yield put({
            type: 'goToFeature',
            payload: {
              featureId: feature.uuid,
              layerId
            }
          })
        }
      }
    }
  }
}

export function* renderLabel({ payload }, { select, put, call }) {
  const { mapRef } = yield select(model => model.map)

  const { label, layerId } = payload

  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      const featureRendered = layer.graphics.items.find(i => i.attributes.uuid === label.id)
      if (!featureRendered) {
        const graphic = yield call(getLabelGraphic, label)
        if (graphic != null) {
          yield put({
            type: 'addGraphicToMap',
            payload: {
              graphic,
              layerId
            }
          })
        }
      }
    }
  }
}

export function* renderFeatureGraphicById({ payload }, { select, put }) {
  const { featuresList } = yield select(model => model.features)
  const { mapRef } = yield select(model => model.map)
  const { featureId, layerId } = payload

  const feature = featuresList.find(fl => fl.uuid === featureId)
  if (mapRef && feature) {
    yield put({
      type: 'renderFeatureGraphic',
      payload: {
        feature,
        layerId
      }
    })
  }
}

export function* removeGraphicByFeatureId({ payload }, { select, put }) {
  const { mapRef } = yield select(model => model.map)
  const { featureId, layerId } = payload

  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      const graphic = layer.graphics.items.find(i => i.attributes.uuid === featureId)
      if (graphic) {
        yield put({
          type: 'removeGraphicFromMap',
          payload: {
            layerId,
            graphic
          }
        })
      }
    }
  }
}

export function* removeGraphicFromMap({ payload }, { select }) {
  const { mapRef } = yield select(model => model.map)
  const { graphic, layerId } = payload

  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      layer.remove(graphic)
    }
  }
}

export function* updateFeatureGraphicAttributes({ payload }, { select, call }) {
  const { mapRef } = yield select(model => model.map)
  // const viewRef = yield select(getActiveViewRef);
  const { attributes, featureId, layerId } = payload

  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    const graphic = layer.graphics.find(g => g.attributes.uuid === featureId)
    if (graphic) {
      graphic.attributes = {
        id: featureId,
        ...graphic.attributes,
        ...attributes
      }
    }
  }
}

export function* updateFeatureGraphic({ payload }, { select, call }) {
  const { mapRef } = yield select(model => model.map)
  const { feature, layerId } = payload

  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    const graphicIndex = layer.graphics.findIndex(g => g.attributes.uuid === feature.uuid)
    if (graphicIndex > -1) {
      const graphic = layer.graphics.items[graphicIndex]
      graphic.symbol = getFeatureSymbol(feature)
      if (feature.properties.ghType === FEATURE_TYPES.Point) {
        graphic.geometry = {
          type: 'point',
          longitude: feature.geometry.coordinates[0],
          latitude: feature.geometry.coordinates[1],
          spatialReference: {
            wkid: 3857 // This is important for when editing waypoint location.
          }
        }
      }
    }
  }
}

export function* addGraphicToMap({ payload }, { select }) {
  const { mapRef } = yield select(model => model.map)
  const { graphic, layerId } = payload

  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      /*
        #NOTE: Doing this will allow waypoints to stay above lines and polygons.
        -- TJC - December 02, 2021
      */
      if (graphic.attributes.properties != null && graphic.attributes.properties.ghType === FEATURE_TYPES.Point) {
        layer.graphics = [...layer.graphics, graphic]
      } else {
        layer.graphics = [graphic, ...layer.graphics]
      }
    }
  }
}

export function* goTo({ payload }, { select }) {
  const viewRef = yield select(getActiveViewRef)
  const viewMode = yield select(model => model.map.mapMode)
  if (viewRef) {
    viewRef.goTo(
      {
        ...payload
      },
      { speedFactor: viewMode == MAP_MODES.ThreeD ? 2 : 1 }
    )
  }
}

export function* goToExtentOfFeatures({ payload: featureIds }, { select, put }) {
  const mapRef = yield select(model => model.map.mapRef)
  const layer = mapRef.findLayerById(MAP_LAYER_IDS.content)

  if (mapRef && layer) {
    const graphicIndex = layer.graphics.items.findIndex(i => featureIds.includes(i.attributes.uuid))
    if (graphicIndex > -1) {
      yield put({ type: 'goTo', payload: { target: layer.graphics.items[graphicIndex], zoom: 15 } })
    }
  }
}

export function* goToFeature({ payload }, { select, put }) {
  const mapRef = yield select(model => model.map.mapRef)
  const { featureId, layerId = MAP_LAYER_IDS.content } = payload

  const layer = mapRef.findLayerById(layerId)
  if (mapRef && layer) {
    const graphicIndex = layer.graphics.items.findIndex(i => i.attributes.uuid === featureId)
    if (graphicIndex > -1) {
      yield put({ type: 'goTo', payload: { target: layer.graphics.items[graphicIndex], zoom: 15 } })
    }
  }
}

export function* goToState({ payload: stateName }, { put }) {
  stateName = stateName.replace('_', ' ')
  yield put({
    type: 'goTo',
    payload: { target: STATES_DATA[stateName].center, zoom: 6.5 }
  })
}

export function* geocodeMapCenter({ callback }, { select, call }) {
  const viewRef = yield select(getActiveViewRef)

  if (viewRef.center) {
    const centerLat = viewRef.center.latitude
    const centerLong = viewRef.center.longitude

    let response = yield call(reverseGeocode, `${centerLong},${centerLat}`)

    if (response.success) {
      callback(response.data.data)
    }
  }
}

export function* clearLayer({ payload: layerId }, { select, put }) {
  const { mapRef } = yield select(model => model.map)

  if (mapRef) {
    const layer = mapRef.findLayerById(layerId)
    if (layer) {
      layer.removeAll()
    }
  }
}

export function* setBasemap({ callback = noop, payload }, { select, put }) {
  const { basemaps, mapRef } = yield select(model => model.map)
  const basemapConfig = basemaps.find(bm => bm.name == payload?.name) || basemaps[0]

  if (basemapConfig && mapRef) {
    const baseLayers = mapRef.basemap.baseLayers
  
    const isHybrid = basemapConfig.name === BASEMAPS.Hybrid
    const isTopographic = basemapConfig.name === BASEMAPS.Topographic
    const newBasemapLayerId = MAP_LAYER_IDS.basemap[basemapConfig.name.toLowerCase()]

    const contourContentLayer = mapRef.findLayerById(MAP_LAYER_IDS.contour)
    const referenceLabelsContentLayer = mapRef.findLayerById(MAP_LAYER_IDS.referenceLabels)
    const referenceLinesContentLayer = mapRef.findLayerById(MAP_LAYER_IDS.referenceLines)

    for (let baseLayer of baseLayers) {
      if (baseLayer) {
        baseLayer.visible = baseLayer.id === newBasemapLayerId
        if (baseLayer.id === MAP_LAYER_IDS.hillshade) {
          baseLayer.visible = isTopographic
        }
      }
    }
    if (contourContentLayer) contourContentLayer.visible = isHybrid
    if (referenceLabelsContentLayer) referenceLabelsContentLayer.visible = !isTopographic
    if (referenceLinesContentLayer) referenceLinesContentLayer.visible = !isTopographic
    yield put({ type: 'setSelectedBasemap', payload: basemapConfig.name })
    callback(basemapConfig?.name)
  } else {
    callback(null)
  }
}

export function* closePopup({ payload }, { select, put }) {
  const viewRef = yield select(getActiveViewRef)

  if (viewRef != null) {
    viewRef.popup.close()
  }
}

export function* cancelSelectSketch({ _ }, { call, select, put }) {
  const selectSketchWidget = yield select(model => model.map.widgetRefs.selectSketch)

  if (selectSketchWidget) {
    selectSketchWidget.cancel()
  }
}

export function* removeGraphicFromLayer({ payload }, { select }) {
  const { mapRef } = yield select(model => model.map)
  if (mapRef) {
    const layer = mapRef.findLayerById(payload.layerId)
    layer.remove(payload.graphic)
  }
}
